import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { z } from "zod";
import { IncidentType } from "@/gql/graphql";
import { Upload } from "@/lib/FileUpload/FileUploader";
import { SUPPLIER_MIN_PHOTOS } from "@/app/Incidents/Utils/supplierReporting";
import {
  incidentClaimShape,
  requireIncidentClaim,
} from "@/app/Incidents/Forms/incidentClaimSchema";

type Translate = ReturnType<typeof useTranslations<"incident">>;

export function foundAtSchema(t: Translate) {
  const required = t("report.required");

  return z
    .string({ error: required })
    .min(1, required)
    .refine(
      (foundAt) => !dayjs(foundAt).isAfter(dayjs()),
      t("report.foundInFuture"),
    );
}

export function getReportIncidentSchema(isAdmin: boolean, t: Translate) {
  const required = t("report.required");
  const minPhotos = isAdmin ? 0 : SUPPLIER_MIN_PHOTOS;
  const claimIsComplete = requireIncidentClaim({
    excessRequired: t("claim.excessRequired"),
    documentsRequired: t("claim.documentsRequired"),
  });

  return z
    .object({
      work_order_id: z.string({ error: required }).min(1, required),
      work_order_assignment_id: z.string({ error: required }).min(1, required),
      found_at: foundAtSchema(t),
      type: z.enum(IncidentType),
      description: z.string().trim().min(1, required).max(5000),
      follow_up_contact: z.string().trim().min(1, required).max(255),
      photos: z
        .array(z.custom<Upload>())
        .min(minPhotos, t("report.minPhotos", { count: minPhotos })),
      open_claim: z.boolean(),
      ...incidentClaimShape,
    })
    .superRefine((values, ctx) => {
      if (values.open_claim) {
        claimIsComplete(values, ctx);
      }
    });
}

export type ReportIncidentFormValues = z.infer<
  ReturnType<typeof getReportIncidentSchema>
>;
